import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Plus } from "lucide-react";
import Header from "../sections/Header";
import Footer from "../sections/Footer";
import { Reveal } from "../components/Reveal";

const PRODUCTS = [
    ["Vinyl Banners", "Indoor and outdoor banners, hemmed and grommeted, ready to hang."],
    ["Event Banners", "Step-and-repeats, stage backdrops and pop-up signage for show day."],
    ["Posters", "Photographic posters up to 64 inches wide — gallery paper, serious color."],
    ["Wall Murals & Wraps", "Full-wall vinyl murals and wraps, printed in panels with minimal seams."],
    ["Window Graphics", "Storefront decals, hours, logos and full window coverage."],
    ["Floor Graphics", "Slip-rated vinyl floor graphics that survive foot traffic."],
    ["Decals & Stickers", "Die-cut decals and sticker runs, any shape, any quantity."],
    ["Backlit Prints", "Film prints for lightboxes — color that stays loud when lit."],
    ["Trade Show Graphics", "Booth walls, counters and display graphics sized to your structure."],
    ["Canvas Prints", "Photographic-quality canvas for lobbies, offices and retail walls."],
];

const PROOF = [
    ["/images/print-mural.jpg", "3D skull mural print — layered acrylic, photographic-quality output"],
    ["/images/print-counter.jpg", "Full-bleed counter wrap — color that carries across panels"],
    ["/images/print-door.jpg", "Storefront door decals — logo, address and hours on glass"],
];

export default function PrintPage() {
    useEffect(() => {
        const prevTitle = document.title;
        document.title = "The Print Shop — Large-Format Printing | Triple O Creative Industries";
        let meta = document.querySelector('meta[name="description"]');
        const prevDesc = meta?.getAttribute("content") || "";
        if (meta)
            meta.setAttribute(
                "content",
                "Large-format printing in Southern California on a 64-inch Epson SureColor S80600 photographic printer. Vinyl banners, event banners, wall murals, window graphics, floor graphics, decals, backlit prints, trade show graphics and canvas prints — print-only orders welcome, no install required."
            );
        const ld = {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Large-Format Printing — The Print Shop at Triple O",
            provider: {
                "@type": "Organization",
                name: "Triple O Creative Industries",
                url: window.location.origin,
            },
            areaServed: "Southern California",
            keywords: PRODUCTS.map(([p]) => p).join(", "),
            description:
                "Photographic-quality large-format printing on an Epson SureColor S80600. Print-only orders welcome.",
        };
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.dataset.projectSeo = "true";
        script.textContent = JSON.stringify(ld);
        document.head.appendChild(script);
        return () => {
            document.title = prevTitle;
            if (meta && prevDesc) meta.setAttribute("content", prevDesc);
            script.remove();
        };
    }, []);

    return (
        <div id="top" data-testid="print-page" className="relative bg-ink text-paper">
            <div className="noise-overlay" aria-hidden="true" />
            <Header />
            <main className="pt-28 lg:pt-36">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-acid">
                            The Print Shop
                        </p>
                        <h1
                            data-testid="print-page-title"
                            className="mt-6 font-display text-4xl font-bold uppercase leading-[1.05] tracking-tight text-paper sm:text-6xl lg:text-7xl"
                        >
                            Large-format print,
                            <br />
                            <span className="text-acid">photographic grade.</span>
                        </h1>
                        <p className="mt-6 max-w-2xl text-base leading-relaxed text-fog sm:text-lg">
                            Need print without the build? No problem. Send us the file and we ship
                            the print — anywhere in the U.S. and international — banners, murals,
                            wraps, decals and more, produced in-house on photographic hardware. No
                            install required, no job too small.
                        </p>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <div className="relative mt-14 border border-white/10 bg-panel p-8 sm:p-12">
                            <span className="absolute left-3 top-3 h-5 w-5 border-l-2 border-t-2 border-acid" aria-hidden="true" />
                            <span className="absolute right-3 top-3 h-5 w-5 border-r-2 border-t-2 border-acid" aria-hidden="true" />
                            <span className="absolute bottom-3 left-3 h-5 w-5 border-b-2 border-l-2 border-acid" aria-hidden="true" />
                            <span className="absolute bottom-3 right-3 h-5 w-5 border-b-2 border-r-2 border-acid" aria-hidden="true" />
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-acid">
                                The Machine
                            </p>
                            <h2 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight text-paper sm:text-4xl">
                                Epson SureColor S80600
                            </h2>
                            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-fog sm:text-base">
                                A 64-inch, 10-color photographic large-format printer — the kind of
                                machine museums print on. Deep blacks, neon brights, gradients that
                                stay smooth at ten feet tall. It's the same hardware behind our
                                booth murals, counter wraps and storefront graphics, and it's
                                available for your print-only job.
                            </p>
                            <div className="mt-6 flex flex-wrap gap-2">
                                {["64-inch width", "10-color photographic", "Photographic quality", "Print-only welcome"].map(
                                    (s) => (
                                        <span
                                            key={s}
                                            className="border border-acid/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-acid"
                                        >
                                            {s}
                                        </span>
                                    )
                                )}
                            </div>
                        </div>
                    </Reveal>

                    <div className="py-20 lg:py-24">
                        <Reveal>
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-acid">
                                What You Can Order
                            </p>
                        </Reveal>
                        <div
                            data-testid="print-products-grid"
                            className="mt-10 grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3"
                        >
                            {PRODUCTS.map(([name, desc], i) => (
                                <Reveal key={name} delay={0.04 * i} className="h-full">
                                    <div className="flex h-full flex-col bg-ink p-6">
                                        <p className="flex items-start gap-3 font-display text-lg font-bold uppercase tracking-tight text-paper">
                                            <Plus className="mt-1 h-4 w-4 shrink-0 text-acid" aria-hidden="true" />
                                            {name}
                                        </p>
                                        <p className="mt-3 text-sm leading-relaxed text-fog">{desc}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>

                    <Reveal>
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-acid">
                            Fresh Off the Printer
                        </p>
                        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-3">
                            {PROOF.map(([src, alt]) => (
                                <div key={src} className="overflow-hidden border border-white/10">
                                    <img
                                        src={src}
                                        alt={alt}
                                        loading="lazy"
                                        className="aspect-[4/3] w-full object-cover"
                                    />
                                </div>
                            ))}
                        </div>
                    </Reveal>

                    <Reveal>
                        <div className="my-24 border border-acid/40 bg-panel p-10 text-center sm:p-16">
                            <p className="font-display text-2xl font-bold uppercase tracking-tight text-paper sm:text-4xl">
                                Send the file. We ship the print — anywhere.
                            </p>
                            <Link
                                to="/#contact"
                                data-testid="print-page-quote-cta"
                                className="group mt-8 inline-flex items-center gap-3 bg-acid px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-paper"
                            >
                                Get a Print Quote
                                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </main>
            <Footer />
        </div>
    );
}
